
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Database } from '@/integrations/supabase/types';

type Consultation = Database['public']['Tables']['consultations']['Row'];
type ConsultationInsert = Database['public']['Tables']['consultations']['Insert'];

export const useConsultations = (userId?: string) => {
  return useQuery({
    queryKey: ['consultations', userId],
    queryFn: async () => {
      if (!userId) return [];
      
      const { data, error } = await supabase
        .from('consultations')
        .select(`
          *,
          mediators!inner(
            profiles!inner(full_name, email)
          )
        `)
        .or(`seeker_id.eq.${userId},mediator_id.eq.${userId}`)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data;
    },
    enabled: !!userId,
  });
};

export const useCreateConsultation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (consultation: ConsultationInsert) => {
      const { data, error } = await supabase
        .from('consultations')
        .insert(consultation)
        .select()
        .single();
      
      if (error) throw error;

      // Send email notification to mediator (fire and forget)
      supabase.functions.invoke('send-new-consultation-email', {
        body: {
          consultationId: data.id,
        },
      }).catch((emailError) => {
        console.error('Failed to send consultation notification email:', emailError);
      });

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['consultations'] });
    },
  });
};

export const useCancelConsultation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (consultationId: string) => {
      // Setting status to 'cancelled' triggers release_slots_on_consultation_cancel
      // server-side, which frees all linked availability_slots automatically.
      const { data, error } = await supabase
        .from('consultations')
        .update({ status: 'cancelled' })
        .eq('id', consultationId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      // Refresh consultations and availability across all relevant views
      queryClient.invalidateQueries({ queryKey: ['consultations'] });
      queryClient.invalidateQueries({ queryKey: ['myConsultations'] });
      queryClient.invalidateQueries({ queryKey: ['mediatorConsultations'] });
      queryClient.invalidateQueries({ queryKey: ['availableSlots'] });
      queryClient.invalidateQueries({ queryKey: ['availabilitySlots'] });
      queryClient.invalidateQueries({ queryKey: ['publicMediatorSlots'] });
      queryClient.invalidateQueries({ queryKey: ['searchAvailabilitySlots'] });
      queryClient.invalidateQueries({ queryKey: ['widgetSearchAvailabilitySlots'] });
    },
  });
};

/**
 * Create a follow-up consultation (meeting_number ≥ 2) for an existing
 * mediator/seeker pair.
 *
 * IMPORTANT — multi-block slot reservation contract:
 *   This hook intentionally does NOT pick an availability slot. The new
 *   consultation row is created with `status = 'pending'` and no
 *   `scheduled_date` / `scheduled_time` / `time_slot_id`. The mediator and
 *   seeker then coordinate the date/time out-of-band (typically via the
 *   in-app messaging) and either reschedule manually or — once a slot
 *   picker for follow-ups is built — must call the same atomic
 *   `reserve_consecutive_slots` RPC used by the first-meeting flow with
 *   N = ceil(mediation_meeting_duration_min / 30) blocks.
 *
 * Today's platform invariant: meeting_number is strictly 1 or 2. We refuse
 * any attempt to create a 3rd-or-later session so that downstream code
 * (Stripe webhook, receipts, emails) never has to deal with unexpected
 * values.
 */
export const useCreateFollowUpConsultation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (sourceConsultation: {
      seeker_id: string;
      mediator_id: string;
      mediation_area: Database['public']['Enums']['mediation_area'];
      meeting_number: number | null;
      other_party_email?: string | null;
      other_party_name?: string | null;
    }) => {
      const nextMeetingNumber = (sourceConsultation.meeting_number || 1) + 1;
      if (nextMeetingNumber !== 2) {
        throw new Error(
          "Une seule séance de suivi (médiation, n°2) peut être planifiée à partir d'une séance d'information. " +
            'Pour des séances supplémentaires, créez une nouvelle réservation.',
        );
      }

      const { data, error } = await supabase
        .from('consultations')
        .insert({
          seeker_id: sourceConsultation.seeker_id,
          mediator_id: sourceConsultation.mediator_id,
          mediation_area: sourceConsultation.mediation_area,
          meeting_number: nextMeetingNumber,
          other_party_email: sourceConsultation.other_party_email,
          other_party_name: sourceConsultation.other_party_name,
          status: 'pending',
          // No scheduled_date / scheduled_time / time_slot_id on purpose:
          // follow-ups do not consume availability_slots until the parties
          // agree on a date — see the slot reservation contract above.
        })
        .select()
        .single();

      if (error) throw error;

      // Notify mediator about the new follow-up consultation
      supabase.functions.invoke('send-new-consultation-email', {
        body: { consultationId: data.id },
      }).catch(console.error);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['consultations'] });
      queryClient.invalidateQueries({ queryKey: ['mediatorConsultations'] });
    },
  });
};

/**
 * Atomically book N consecutive availability blocks for an EXISTING follow-up
 * consultation (meeting_number = 2). Mirrors the first-meeting flow on
 * SeekerPage but operates on a row that already exists.
 *
 * Steps:
 *   1. Call `reserve_consecutive_slots(consultation_id, slot_ids)` — the RPC
 *      locks the rows under SELECT … FOR UPDATE and refuses if any block was
 *      taken concurrently or if continuity is broken.
 *   2. Update the consultation row with scheduled_date / scheduled_time /
 *      time_slot_id pointing at the FIRST of the N booked blocks.
 *
 * If the UPDATE fails after the RPC succeeded, the slots are released by
 * cancelling the consultation so we never leave a "reserved-but-unscheduled"
 * window in the DB.
 */
export const useScheduleFollowUpSlot = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      consultationId: string;
      slotIds: string[];
      startSlot: { date: string; start_time: string };
    }) => {
      const { consultationId, slotIds, startSlot } = params;

      const { error: reserveError } = await supabase.rpc('reserve_consecutive_slots', {
        p_consultation_id: consultationId,
        p_slot_ids: slotIds,
      });

      if (reserveError) {
        const wrapped = new Error(reserveError.message || 'reservation_failed');
        (wrapped as Error & { __reserveSlots?: boolean }).__reserveSlots = true;
        throw wrapped;
      }

      const { data, error } = await supabase
        .from('consultations')
        .update({
          scheduled_date: startSlot.date,
          scheduled_time: startSlot.start_time,
          time_slot_id: slotIds[0],
        })
        .eq('id', consultationId)
        .select()
        .single();

      if (error) {
        // Roll back the reservation by cancelling — the
        // release_slots_on_consultation_cancel trigger frees all N blocks.
        await supabase
          .from('consultations')
          .update({ status: 'cancelled' })
          .eq('id', consultationId);
        throw error;
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['consultations'] });
      queryClient.invalidateQueries({ queryKey: ['myConsultations'] });
      queryClient.invalidateQueries({ queryKey: ['mediatorConsultations'] });
      queryClient.invalidateQueries({ queryKey: ['availableSlots'] });
      queryClient.invalidateQueries({ queryKey: ['availabilitySlots'] });
      queryClient.invalidateQueries({ queryKey: ['publicMediatorSlots'] });
      queryClient.invalidateQueries({ queryKey: ['searchAvailabilitySlots'] });
    },
  });
};
